import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useMeta } from '../meta';
import { DangerZone, ErrorBox, Panel, fmtDate } from '../components';
import { TelegramAccountPanel } from './ManageConnect';
import { LANGUAGES, N_, getLanguage, setLanguage, t } from '../i18n';
import { usePublicConfig } from './Auth';
import { UpdateTab } from './ManageUpdate';
import { AttachmentPreviewSettings } from '../AttachmentPreviewSettings';
import { attachmentPreviewSize } from '../../../shared/attachmentPreview';

const PREFS: [string, string][] = [
  ['email_on_new', N_('E-mail on New')],
  ['email_on_assigned', N_('E-mail on Change of Handler')],
  ['email_on_feedback', N_('E-mail on Feedback')],
  ['email_on_resolved', N_('E-mail on Resolved')],
  ['email_on_closed', N_('E-mail on Closed')],
  ['email_on_reopened', N_('E-mail on Reopened')],
  ['email_on_bugnote', N_('E-mail on Note Added')],
  ['email_on_status', N_('E-mail on Status Change')],
  ['email_on_priority', N_('E-mail on Priority Change')],
];

export function AccountPage() {
  const mantis = usePublicConfig().data?.mantis_database === true;
  const { meta, label } = useMeta();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['account'], queryFn: () => api.get('/api/account') });
  const [realname, setRealname] = useState('');
  const [email, setEmail] = useState('');
  const [current, setCurrent] = useState('');
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [prefs, setPrefs] = useState<Record<string, any> | null>(null);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (!data) return;
    setRealname(data.user.realname);
    setEmail(data.user.email);
    setPrefs(data.prefs);
  }, [data]);

  const saveAccount = useMutation({
    mutationFn: () => {
      if (pw1 && pw1 !== pw2) throw new Error(t('The new passwords do not match.'));
      return api.patch('/api/account', { realname, email, ...(pw1 ? { current_password: current, new_password: pw1 } : {}) });
    },
    onSuccess: (r: { email_confirmation_sent?: boolean }) => {
      setMsg(r?.email_confirmation_sent ? t('Account updated. We sent a link to the new e-mail address to confirm it.') : t('Account updated.'));
      setCurrent('');
      setPw1('');
      setPw2('');
      qc.invalidateQueries({ queryKey: ['meta'] });
      qc.invalidateQueries({ queryKey: ['account'] });
    },
  });
  // the language applies at once (no "Update Prefs" needed)
  const saveLanguage = useMutation({
    mutationFn: async (language: string) => {
      await api.put('/api/account/language', { language });
      return language;
    },
    onSuccess: (language) => {
      qc.invalidateQueries({ queryKey: ['session'] });
      qc.invalidateQueries({ queryKey: ['meta'] });
      setLanguage(language);
    },
  });
  const savePrefs = useMutation({
    mutationFn: () =>
      api.put('/api/account/prefs', {
        ...Object.fromEntries(PREFS.map(([k]) => [k, !!prefs?.[k]])),
        default_project_id: prefs?.default_project_id || null,
        bugnote_order: prefs?.bugnote_order ?? 'ASC',
        color_issue_rows: prefs?.color_issue_rows !== false,
        dark_mode: prefs?.dark_mode === true,
        attachment_preview_size: attachmentPreviewSize(prefs?.attachment_preview_size),
      }),
    onSuccess: () => {
      setMsg(t('Preferences saved.'));
      qc.invalidateQueries({ queryKey: ['meta'] });
      qc.invalidateQueries({ queryKey: ['account'] });
    },
  });

  if (!data || !prefs) return <div className="muted">{t('Loading…')}</div>;
  return (
    <div>
      <div className="page-head">
        <h1>{t('My Account')}</h1>
      </div>
      {msg && <div className="alert ok">{msg}</div>}
      <div className="two-col">
        <Panel title={t('Edit Account')}>
          <form
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              setMsg('');
              saveAccount.mutate();
            }}
          >
            <div className="form-grid">
              <label>
                {t('Username')}
                <input value={data.user.username} disabled />
              </label>
              <label>
                {t('Access Level')}
                <input value={label('access_level', data.user.access_level)} disabled />
              </label>
              <label>
                {t('Real Name')}
                <input value={realname} onChange={(e) => setRealname(e.target.value)} />
              </label>
              <label>
                {t('E-mail')}
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </label>
              <label>
                {t('Current Password')}
                <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
              </label>
              <span />
              <label>
                {t('New Password')}
                <input type="password" value={pw1} onChange={(e) => setPw1(e.target.value)} autoComplete="new-password" />
              </label>
              <label>
                {t('Confirm Password')}
                <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" />
              </label>
            </div>
            <ErrorBox error={saveAccount.error} />
            <button className="primary" disabled={saveAccount.isPending}>
              {t('Update User')}
            </button>
          </form>
          <p className="muted small">
            {t('Member since {date} · last visit {visit}', { date: fmtDate(data.user.created_at, false), visit: fmtDate(data.user.last_visit) })}
          </p>
          {data.projects.length > 0 && (
            <>
              <h3>{t('Assigned Projects')}</h3>
              <ul>
                {data.projects.map((p: any) => (
                  <li key={p.id}>
                    {p.name} — {label('access_level', p.access_level)}
                  </li>
                ))}
              </ul>
            </>
          )}
        </Panel>
        <Panel title={t('Preferences')}>
          <label className="block language-pref">
            {t('Language')}
            <select
              value={getLanguage()}
              disabled={saveLanguage.isPending}
              onChange={(e) => saveLanguage.mutate(e.target.value)}
              aria-label={t('Language')}
            >
              {LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.label}
                </option>
              ))}
            </select>
          </label>
          <ErrorBox error={saveLanguage.error} />
          <form
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              setMsg('');
              savePrefs.mutate();
            }}
          >
            <label className="block">
              {t('Default Project')}
              <select value={prefs.default_project_id ?? ''} onChange={(e) => setPrefs({ ...prefs, default_project_id: e.target.value ? Number(e.target.value) : null })}>
                <option value="">{t('All Projects')}</option>
                {meta.projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              {t('Notes Sort Order')}
              <select value={prefs.bugnote_order} onChange={(e) => setPrefs({ ...prefs, bugnote_order: e.target.value })}>
                <option value="ASC">{t('Ascending (oldest first)')}</option>
                <option value="DESC">{t('Descending (newest first)')}</option>
              </select>
            </label>
            <AttachmentPreviewSettings value={prefs.attachment_preview_size} onChange={(size) => setPrefs({ ...prefs, attachment_preview_size: size })} />
            <div className="checks">
              <label className="check">
                <input type="checkbox" checked={prefs.dark_mode === true} onChange={(e) => setPrefs({ ...prefs, dark_mode: e.target.checked })} />
                {t('Dark mode')}
              </label>
              <label className="check">
                <input type="checkbox" checked={prefs.color_issue_rows !== false} onChange={(e) => setPrefs({ ...prefs, color_issue_rows: e.target.checked })} />
                {t('Color issue rows by status')}
              </label>
              {PREFS.map(([k, l]) => (
                <label key={k} className="check">
                  <input type="checkbox" checked={!!prefs[k]} onChange={(e) => setPrefs({ ...prefs, [k]: e.target.checked })} /> {t(l)}
                </label>
              ))}
            </div>
            <ErrorBox error={savePrefs.error} />
            <button className="primary" disabled={savePrefs.isPending}>
              {t('Update Prefs')}
            </button>
          </form>
        </Panel>
      </div>
      {meta.can_update_application && <UpdateTab />}
      <TelegramAccountPanel />
      {!mantis && <DeleteAccount username={meta.user.username} />}
    </div>
  );
}

function DeleteAccount({ username }: { username: string }) {
  const { data } = useQuery({
    queryKey: ['account-delete-check'],
    queryFn: () => api.get<{ deleted_teams: { id: number; name: string }[]; blocking_teams: { id: number; name: string }[] }>('/api/account/delete-check'),
  });
  if (!data) return null;
  const names = (list: { name: string }[]) => list.map((x) => `"${x.name}"`).join(', ');
  return (
    <DangerZone
      title={t('Delete account')}
      button={t('Delete my account')}
      confirmText={username}
      needPassword
      disabled={data.blocking_teams.length > 0}
      onConfirm={async (password) => {
        await api.post('/api/account/delete', { password });
        location.href = '/login';
      }}
    >
      <p>
        {t('Removes your name, e-mail address, password and memberships. Issues and notes you wrote stay in their teams and show "Deleted user". This cannot be undone.')}
      </p>
      {data.deleted_teams.length > 0 && (
        <div className="banner warning">
          {t('You are the only member of {teams}; deleting your account deletes these teams with all their projects and issues.', { teams: names(data.deleted_teams) })}
        </div>
      )}
      {data.blocking_teams.length > 0 && (
        <div className="banner error">
          {t('You are the only administrator of {teams}. Make another member an administrator (Manage → Team) or delete the team first.', { teams: names(data.blocking_teams) })}
        </div>
      )}
    </DangerZone>
  );
}
